import { COMPONENT_RENDERER_TYPE } from '@uber/atgl-components';
import type { MediaFile } from '../components/media-input';

/**
 * Determines the media type from a File object
 */
export const getMediaType = (file: File): 'image' | 'video' | 'pdf' | null => {
  if (file.type.startsWith('image/')) {
    return 'image';
  }
  if (file.type.startsWith('video/')) {
    return 'video';
  }
  if (file.type === 'application/pdf') {
    return 'pdf';
  }
  return null;
};

/**
 * Processes accepted files and converts them to MediaFile objects with preview URLs
 * @param acceptedFiles - Array of File objects to process
 * @param currentFileCount - Current number of files already in the list
 * @param maxFiles - Maximum number of files allowed
 * @returns Array of MediaFile objects
 */
export const processMediaFiles = (acceptedFiles: File[], currentFileCount: number, maxFiles: number): MediaFile[] => {
  const newMediaFiles: MediaFile[] = [];
  acceptedFiles.forEach(file => {
    const fileType = getMediaType(file);
    if (fileType && currentFileCount + newMediaFiles.length < maxFiles) {
      const preview = URL.createObjectURL(file);
      newMediaFiles.push({
        file,
        preview,
        type: fileType
      });
    }
  });
  return newMediaFiles;
};

/**
 * Revokes object URLs to free up memory
 * @param mediaFiles - Array of MediaFile objects whose previews should be revoked
 */
export const revokeMediaFileURLs = (mediaFiles: MediaFile[]): void => {
  mediaFiles.forEach(mediaFile => {
    URL.revokeObjectURL(mediaFile.preview);
  });
};

/**
 * Creates a new array of MediaFiles by adding new files to existing ones
 * @param existingFiles - Current MediaFile array
 * @param newFiles - Array of File objects to add
 * @param maxFiles - Maximum number of files allowed
 * @returns Updated array of MediaFile objects
 */
export const addMediaFiles = (existingFiles: MediaFile[], newFiles: File[], maxFiles: number): MediaFile[] => {
  const processedFiles = processMediaFiles(newFiles, existingFiles.length, maxFiles);
  return [...existingFiles, ...processedFiles];
};

/**
 * Removes a MediaFile at a specific index and revokes its URL
 * @param mediaFiles - Current MediaFile array
 * @param index - Index of the file to remove
 * @returns Updated array of MediaFile objects
 */
export const removeMediaFile = (mediaFiles: MediaFile[], index: number): MediaFile[] => {
  const fileToRemove = mediaFiles[index];
  if (fileToRemove) {
    URL.revokeObjectURL(fileToRemove.preview);
  }
  return mediaFiles.filter((_, i) => i !== index);
};
export const getMimeType = (type: COMPONENT_RENDERER_TYPE) => {
  switch (type) {
    case COMPONENT_RENDERER_TYPE.IMAGE:
      return 'image/*';
    case COMPONENT_RENDERER_TYPE.VIDEO:
      return 'video/*';
    case COMPONENT_RENDERER_TYPE.AUDIO:
      return 'audio/*';
    case COMPONENT_RENDERER_TYPE.PDF:
      return 'application/pdf';
  }
};